import * as Config from '../../config';
import { default as Locale } from '../../utils/Locale';
import * as UUID from '../../utils/Uuid';
import { AlertInfo, AlertType, AlertRecord, AlertCategories, CardConfig } from "../Types";
import { AlertLogic } from './AlertLogic';
import { getPumpInfo, getHeaterInfo, getSaltCellCatalogEntry } from '../../catalog';
import { ContentCategories } from "../../types";


export class NotResponding extends AlertLogic {
    static type = AlertType.NotResponding;
    static category = AlertCategories.EQUIPMENT_FAILURE;
    static contentCategories = [ ContentCategories.SYSTEM_ERROR ];
    static title = Locale.gettext('Not Responding');

    getInfo(alert:AlertRecord = this.alert, config:Config.AllConfig = this.allConfigs):AlertInfo {
        // Same for all alert types
        let { body, device, alertData, firstReported } = this.getCommons(alert, config);
        // Varying across alert types
        let detailIcon = 'alert';

        let lastSeen = alertData?.OfflineDateTime ? Locale.gettext("Device last seen: %1$s", Locale.formatDateTime(new Date(alertData?.OfflineDateTime), false)) : "";
        const isOfflineToSecondary = alertData?.OfflineToSecondary === true;

        let kbUrl = '';
        if (isOfflineToSecondary) {
            kbUrl = 'https://poolside.support/contact-us/';
        } else {
            switch (device?.DeviceType) {
                case Config.DeviceTypes.PUMP: kbUrl = getPumpInfo(device)?.kbUrl; break;
                case Config.DeviceTypes.SALTCELL: kbUrl = getSaltCellCatalogEntry(
                    device?.HardwareDevice?.PrimaryCatalogId,
                    device?.HardwareDevice?.SecondaryCatalogId
                )?.model?.kbUrl; break;
                case Config.DeviceTypes.HEATER:
                case Config.DeviceTypes.HEATPUMP:
                case Config.DeviceTypes.CHILLER:
                    kbUrl = getHeaterInfo(device)?.kbUrl;
                    break;
            }
        }

        const alertInfo = AlertInfo.fromCardConfig({
            cardTitle: Locale.gettext('Device Not Responding'),
            cardDescription: isOfflineToSecondary ? Locale.gettext('%1$s - secondary Attendant is offline', device?.Name) : Locale.gettext('%1$s is not responding', device?.Name),
            cardIllustration: 'pipes_emergency',
            detailIcon: detailIcon,
            //detailTitle: Locale.gettext('Device not responding'),
            detailDescriptionArr: isOfflineToSecondary ? [
                Locale.gettext('Device "%1$s" %2$s is not responding because a secondary Attendant is offline.', device?.Name, device.HardwareDevice?.UniqueBusIdentifier ? '(Serial port: ' + device.HardwareDevice?.Ports?.[0]?.Number + ', ID: ' + device.HardwareDevice?.UniqueBusIdentifier + ')' : ''),
                Locale.gettext('Please verify the secondary Attendant is powered on, and its communication cables to the primary are plugged in.'),
                Locale.gettext('Contact your pool maintainer or Poolside Tech Support for additional assistance.'),
                lastSeen,
            ] : [
                Locale.gettext('Device "%1$s" %2$s is not responding.', device?.Name, device.HardwareDevice?.UniqueBusIdentifier ? '(Serial port: ' + device.HardwareDevice?.Ports?.[0]?.Number + ', ID: ' + device.HardwareDevice?.UniqueBusIdentifier + ')' : ''),
                Locale.gettext('Please check that it\'s powered on, the communication cables are correctly plugged in, and that you have configured the correct port settings in the configuration.'),
                Locale.gettext('Contact your pool maintainer or the device manufacturer if the problem continues.'),
                lastSeen,
            ],
            actionButtonTitle: kbUrl ? Locale.gettext('Knowledge Base') : '',
            actionButtonAction: kbUrl ? {
                actions: [
                    {
                        type: 'openUrl',
                        url: kbUrl
                    }
                ]
            } : undefined,
            actionButtonVisibleFor: undefined,
            actionButtonEnabledFor: undefined,
            resolveSummary: Locale.gettext('%1$s is back online', device?.Name)
        });
        alertInfo.contentCategories = NotResponding.contentCategories;
        return alertInfo;
    }

    static mock(type:AlertType, config:Config.AllConfig):AlertRecord {
        let device = config.Config?.Devices?.find(dev => dev.DeviceType === "Pump");

        return device ? new AlertRecord({
            id: Math.floor(Math.random() * 100000),
            uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "ALRT" + type),
            siteId: Math.floor(Math.random() * 100000),
            deviceId: 1,
            entityUuid: device?.UUID,
            bodyUuid: config.Config.BodiesOfWater?.[0]?.UUID,
            type: type,
            data: {
                OfflineDateTime: new Date(Date.now() - 60*60*1000),
            }
        }) : null;
    }
}